import { useEffect, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";
import { toast } from "sonner";

async function hasRealConnectivity(): Promise<boolean> {
  if (typeof window === "undefined") return true;
  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 3000);
    const res = await fetch(`/favicon.ico?online-check=${Date.now()}`, {
      method: "HEAD",
      cache: "no-store",
      signal: controller.signal,
    });
    window.clearTimeout(timeout);
    return res.ok || res.type === "opaque";
  } catch {
    return false;
  }
}

export function OfflineBanner() {
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const showOffline = () => {
      setOffline(true);
      toast.error("You're offline. Some features are unavailable.", {
        id: "offline",
        duration: Infinity,
        icon: <WifiOff className="h-4 w-4" />,
      });
    };

    const showOnline = () => {
      setOffline(false);
      toast.dismiss("offline");
      toast.success("Back online", {
        id: "online",
        duration: 2000,
        icon: <Wifi className="h-4 w-4" />,
      });
    };

    const verifyOffline = async () => {
      const online = await hasRealConnectivity();
      if (cancelled) return;
      if (online) {
        setOffline(false);
        toast.dismiss("offline");
      } else {
        showOffline();
      }
    };

    const onOffline = () => {
      void verifyOffline();
    };

    const onOnline = () => {
      showOnline();
    };

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      void verifyOffline();
    }
    return () => {
      cancelled = true;
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  if (!offline) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-amber-950 text-center text-sm font-medium py-1.5 shadow"
    >
      <WifiOff className="inline h-4 w-4 mr-1 align-text-bottom" />
      You're offline. Some features are unavailable.
    </div>
  );
}